import React, { useEffect, useState } from "react";
import { Grid, Button } from "@mui/material";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    BarElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";
import { Banknote, CircleDashed, FileCheck2, ReceiptText } from "lucide-react";
import { getInvoiceReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";
import ReportKpiCards from "./ReportKpiCards";
import {
    areaSeries,
    barChartOptions,
    barSeries,
    ChartLegend,
    ChartPanel,
    legendFromChartData,
    lineChartOptions,
    portalColor,
    ringCenterPlugin,
    ringChartOptions,
    ringSeries,
    statusColor,
} from "./reportCharts";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Filler, Tooltip, Legend);

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "Tripola", value: "tripola" },
            { label: "PayCentral", value: "paycentral" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Paid", value: "paid" },
            { label: "Pending", value: "pending" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const InvoiceReports = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalInvoices: 0,
        paidInvoices: 0,
        pendingInvoices: 0,
        totalAmount: 0,
    });

    const [chartData, setChartData] = useState({
        trend: [],
        status: { paid: 0, pending: 0 },
        productAmount: { tripola: 0, paycentral: 0 },
    });

    const buildApiFilters = (fv) => ({
        product: fv.portal === "all" ? "" : fv.portal,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchInvoiceReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getInvoiceReportAction(buildApiFilters(fv));
            if (response?.status === 200) {
                setSummary(response.data.summary);
                setChartData(response.data.charts);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchInvoiceReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchInvoiceReport(resetValues);
    };

    useEffect(() => {
        fetchInvoiceReport();
    }, []);

    const hasAppliedFilters =
        filterValues.portal !== "all" ||
        filterValues.status !== "all";

    const trendData = {
        labels: chartData?.trend?.map((item) => item.month) || [],
        datasets: [
            areaSeries({
                label: "Invoice Amount",
                data: chartData?.trend?.map((item) => item.amount) || [],
            }),
        ],
    };

    const statusChartData = {
        labels: ["Paid", "Pending"],
        datasets: [
            ringSeries({
                data: [
                    chartData?.status?.paid || 0,
                    chartData?.status?.pending || 0,
                ],
                colors: [statusColor("paid"), statusColor("pending")],
            }),
        ],
    };

    const productChartData = {
        labels: ["Tripola", "PayCentral"],
        datasets: [
            barSeries({
                label: "Invoice Amount",
                data: [
                    chartData?.productAmount?.tripola || 0,
                    chartData?.productAmount?.paycentral || 0,
                ],
                colors: [portalColor("tripola"), portalColor("paycentral")],
            }),
        ],
    };

    const hasTrend = (chartData?.trend || []).length > 0;
    const hasStatus =
        Number(chartData?.status?.paid || 0) + Number(chartData?.status?.pending || 0) > 0;

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Invoice Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "total",
                                label: "Total Invoices",
                                value: summary.totalInvoices ?? 0,
                                accent: "#22419e",
                                icon: <ReceiptText />,
                            },
                            {
                                key: "paid",
                                label: "Paid Invoices",
                                value: summary.paidInvoices ?? 0,
                                accent: "#059669",
                                icon: <FileCheck2 />,
                            },
                            {
                                key: "pending",
                                label: "Pending Invoices",
                                value: summary.pendingInvoices ?? 0,
                                accent: "#d97706",
                                icon: <CircleDashed />,
                            },
                            {
                                key: "amount",
                                label: "Total Amount",
                                value: Number(summary.totalAmount || 0),
                                accent: "#0f766e",
                                icon: <Banknote />,
                            },
                        ]}
                    />
                </Grid>

                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Invoice trend"
                            caption="Invoiced amount per month"
                            height={330}
                            loading={loading}
                            isEmpty={!hasTrend}
                        >
                            <Line data={trendData} options={lineChartOptions()} />
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Invoice status"
                            caption="Paid against pending invoices"
                            height={330}
                            loading={loading}
                            isEmpty={!hasStatus}
                        >
                            <div className="report-ring">
                                <div className="report-ring-chart">
                                    <Doughnut
                                        data={statusChartData}
                                        options={{
                                            ...ringChartOptions(),
                                            plugins: {
                                                ...ringChartOptions().plugins,
                                                reportRingCenter: { caption: "Invoices" },
                                            },
                                        }}
                                        plugins={[ringCenterPlugin]}
                                    />
                                </div>
                                <ChartLegend items={legendFromChartData(statusChartData)} />
                            </div>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12}>
                        <ChartPanel
                            title="Invoice amount by product"
                            caption="Invoiced value split across products"
                            height={320}
                            loading={loading}
                        >
                            <Bar data={productChartData} options={barChartOptions()} />
                        </ChartPanel>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default InvoiceReports;
